import maplibregl from "maplibre-gl"

import Hook from "../utils/hook"

// The base map: the vector Lantmäteriet style the maps app uses (`mapStyles`
// in map-utils' `lib/map.js`), a style JSON whose sources, layers and sprites
// are added below the map's own `base` layer, so the layers of the pages
// using the map (added later, on top) are always above it.
const TILES_ORIGIN = `https://tiles.outdoormap.dev`
const STYLE_URL = `${TILES_ORIGIN}/styles/lantmateriet.json`
const STYLE_PREFIX = `tileset-lantmateriet`
const BASE_LAYER_ID = `base`

// The TileJSON of the vector tilesets may advertise its tiles on the origin
// IP over plain HTTP, which is blocked on HTTPS pages, so those requests are
// rewritten to the domain (`mapTransformRequest` in map-utils)
const TILES_IP_ORIGIN = `http://136.148.208.205`

const transformRequest = (url, resourceType) => {
  if (resourceType == `Tile` && url.startsWith(`${TILES_IP_ORIGIN}/tiles/`))
    return { url: TILES_ORIGIN + url.slice(TILES_IP_ORIGIN.length) }

  return { url }
}

// fetched once per page, whichever map asks first
let styleRequest = null

const fetchStyle = () => {
  styleRequest ||= window.fetch(STYLE_URL).then((response) => {
    if (!response.ok) throw new Error(`${STYLE_URL}: HTTP ${response.status}`)
    return response.json()
  })

  styleRequest.catch(() => (styleRequest = null)) // tried again next time
  return styleRequest
}

export default Hook(`Maplibre`, {
  targets: [`map`, `fullscreen`, `zoomIn`, `zoomOut`],
  values: { fullscreenClass: String },
  actions: [
    {
      target: `zoomIn`,
      name: `zoomIn`,
      type: `click`,
    },
    {
      target: `zoomOut`,
      name: `zoomOut`,
      type: `click`,
    },
    {
      target: `fullscreen`,
      name: `toggleFullscreen`,
      type: `click`,
    },
  ],
  map: null,

  mounted() {
    this.loadHandlers = new Set()

    this.map = new maplibregl.Map({
      container: this.mapTarget,
      style: {
        version: 8,
        id: `naturkartan`,
        name: `Naturkartan`,
        pitch: 0,
        bearing: 0,
        // the app's sprite (the pins) is the default one, its images are
        // named without a sprite id; the base map's sprites have ids
        sprite: `https://map.naturkartan.se/sprite/sprite`,
        glyphs: `https://map.naturkartan.se/glyphs/{fontstack}/{range}.pbf`,
        sources: {
          [BASE_LAYER_ID]: {
            type: `geojson`,
            data: { type: `FeatureCollection`, features: [] },
          },
        },
        layers: [{ id: BASE_LAYER_ID, type: `symbol`, source: BASE_LAYER_ID }],
      },
      center: [14, 63],
      zoom: 4,
      attributionControl: false,
      transformRequest,
    })

    this.map.on(`load`, () => {
      this.addBaseStyle()
      this.loadHandlers.forEach((fn) => fn(this.map))
    })
  },

  destroyed() {
    this.loadHandlers.clear()
    this.map?.remove()
    this.map = null
  },

  addLoadHandler(fn) {
    this.loadHandlers.add(fn)
  },

  removeLoadHandler(fn) {
    this.loadHandlers.delete(fn)
  },

  async addBaseStyle() {
    let style

    try {
      style = await fetchStyle()
    } catch (error) {
      console.error(error)
      return
    }

    const map = this.map
    if (!map?.style) return // the map has been removed meanwhile

    const sprites = Array.isArray(style.sprite) ? style.sprite : []
    sprites.forEach(({ id, url }) => map.addSprite(id, url))

    Object.entries(style.sources).forEach(([name, source]) =>
      map.addSource(`${STYLE_PREFIX}-${name}`, source)
    )

    style.layers.forEach((layer) =>
      map.addLayer(
        {
          ...layer,
          id: `${STYLE_PREFIX}-${layer.id}`,
          ...(layer.source && { source: `${STYLE_PREFIX}-${layer.source}` }),
        },
        BASE_LAYER_ID
      )
    )
  },

  zoomInAction() {
    this.map.zoomIn()
  },

  zoomOutAction() {
    this.map.zoomOut()
  },

  toggleFullscreenAction() {
    if (!this.fullscreenClassValue) return

    this.classToggle(this.fullscreenClassValue)
    this.map?.resize()
  },
})
